/**
 * Sprung zur eigenen vorigen Nachricht (Alex, 2026-08-12).
 *
 * „Wenn ich meine eigenen Nachrichten suche, dann finde ich diese nicht."
 * In einem Verlauf, in dem ein einzelner Agentenzug über den halben Bildschirm
 * geht und dazwischen fremde IACP-Blöcke stehen, ist die eigene letzte Zeile
 * nicht mehr auffindbar — man scrollt an ihr vorbei.
 *
 * WAS ALS EIGENE NACHRICHT ZÄHLT, entscheidet nicht diese Datei: `parseTurnMeta`
 * liefert `sender === null` genau dann, wenn im Zug weder ein `[IACP:…]`- noch
 * ein `[Queue Message …]`-Umschlag steckt. Das ist dieselbe Regel, an der die
 * Anzeige schon hängt — eine zweite Kopie davon wäre genau die Drift, die uns
 * heute an anderer Stelle Stunden gekostet hat.
 *
 * Reine Funktionen über einer Liste von Positionen, damit sich die Auswahl
 * prüfen lässt, ohne einen Browser zu bauen.
 */

/** Die Taste ist bewusst doppelt belegt. */
export type JumpDirection = 'up' | 'down';

export interface JumpKeyEvent {
  key?: string;
  code?: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
}

/**
 * Welche Richtung eine Taste meint — oder keine.
 *
 * Alex hat ausdrücklich Strg+Pfeil verlangt — und **macOS nimmt es ihm weg**:
 * Strg+Pfeil hoch ist Mission Control, Strg+Pfeil runter sind die App-Fenster.
 * Beides fängt das System ab, bevor der Browser überhaupt etwas sieht; kein
 * `preventDefault` der Welt kommt da heran (gemeldet 12.08., „das binding
 * passt nicht").
 *
 * Deshalb gelten drei Formen, und alle drei erreichen die Seite wirklich:
 *
 *   Alt/Option + Pfeil    — auf macOS an nichts vergeben
 *   Strg+Umschalt + Pfeil — Mission Control hängt an Strg OHNE Umschalt
 *   Strg + Pfeil          — bleibt drin für alle, die kein macOS benutzen
 *
 * `metaKey` (Cmd) bleibt ausgenommen: Cmd+Pfeil ist im Browser Anfang und Ende
 * des Dokuments, und das gehört dem Browser.
 */
export function jumpDirectionFor(event: JumpKeyEvent): JumpDirection | null {
  // Cmd bleibt ausgenommen: Cmd+Pfeil ist im Browser Anfang/Ende des Dokuments.
  if (event.metaKey) return null;
  if (!event.ctrlKey && !event.altKey) return null;
  const key = event.code || event.key;
  if (key === 'ArrowUp') return 'up';
  if (key === 'ArrowDown') return 'down';
  return null;
}

/**
 * Der nächste eigene Beitrag in Blickrichtung.
 *
 * `positions` sind die Abstände der eigenen Nachrichten vom oberen Rand des
 * Behälters, aufsteigend. `viewTop` ist die aktuelle Blickhöhe.
 *
 * Die Toleranz verhindert das Steckenbleiben: Nach einem Sprung steht die
 * getroffene Nachricht genau auf `viewTop`, und ohne Abstand wäre sie beim
 * nächsten Druck wieder der „nächste Treffer nach oben".
 */
export const JUMP_TOLERANCE_PX = 8;

export function nextOwnTurnIndex(
  positions: readonly number[],
  viewTop: number,
  direction: JumpDirection,
  tolerance = JUMP_TOLERANCE_PX,
): number | null {
  if (!positions.length) return null;
  if (direction === 'up') {
    for (let i = positions.length - 1; i >= 0; i -= 1) {
      if (positions[i] < viewTop - tolerance) return i;
    }
    // Schon bei der obersten: dort bleiben statt nichts zu tun — sonst wirkt
    // die Taste kaputt, obwohl sie am Ende der Liste angekommen ist.
    return 0;
  }
  for (let i = 0; i < positions.length; i += 1) {
    if (positions[i] > viewTop + tolerance) return i;
  }
  return positions.length - 1;
}


/**
 * Sichtfilter des Verlaufs (Alex, 12.08.).
 *
 * „Kannst du einen Shortcut bauen, wo ich sehe, was ich jetzt habe — oder alle
 * meine Eingaben ohne Antworten der Agenten, oder alle Inputs von mir und alle
 * IACP ohne Antworten, und dann wieder alles?"
 *
 * Drei Stufen im Kreis. Der Filter blendet die ANTWORTEN aus, nicht die Züge:
 * Ein Zug besteht aus Eingabe und Antwort, und gesucht wird die Eingabe.
 */
export type StreamFilter = 'all' | 'own' | 'own_iacp';

export const STREAM_FILTER_CYCLE: readonly StreamFilter[] = ['all', 'own', 'own_iacp'];

export const STREAM_FILTER_LABEL: Record<StreamFilter, string> = {
  all: 'Alles',
  own: 'Nur meine Eingaben',
  own_iacp: 'Meine Eingaben + IACP',
};

export function nextStreamFilter(current: StreamFilter): StreamFilter {
  const i = STREAM_FILTER_CYCLE.indexOf(current);
  return STREAM_FILTER_CYCLE[(i + 1) % STREAM_FILTER_CYCLE.length];
}

/** Erkennt die Taste für den Filterwechsel. Dieselbe Vorsicht wie beim Sprung:
 *  Cmd gehört dem Browser, und Strg allein nimmt macOS an anderer Stelle. */
export function isStreamFilterKey(event: JumpKeyEvent): boolean {
  if (event.metaKey) return false;
  if (!event.ctrlKey && !event.altKey) return false;
  const key = (event.code || event.key || '').toLowerCase();
  return key === 'keyf' || key === 'f';
}

/** Zeigt dieser Zug in der gewählten Stufe überhaupt etwas? */
export function turnVisibleInFilter(
  filter: StreamFilter,
  turn: { hasUser: boolean; isOwn: boolean },
): boolean {
  if (filter === 'all') return true;
  if (!turn.hasUser) return false;
  return filter === 'own_iacp' ? true : turn.isOwn;
}
